import dayjs from 'dayjs';

import { SELECTED_BANK_STATUSES } from '@/constants';

export type TRequestResponseStatusCounts = {
  approvedAlternativesCount: number;
  approvedCount: number;
  clientAcceptedCount: number;
  deniedCount: number;
  errorsCount: number;
  expectationsCount: number;
  financedCount: number;
  fundedDateOfFinanced: string;
  isAddedToQueue: boolean;
  isClientAccepted: boolean;
  isDraft: boolean;
  isError: boolean;
  isFinanced: boolean;
  isSelected: boolean;
  isUnverified: boolean;
  selectedBankName: string;
  waitingFinancingCount: number;
};

const requestResponsesCounter = (
  responses: { status: string; bankName: string; fundedDate: string | null }[],
  requestStatus: string
): TRequestResponseStatusCounts => {
  const result: TRequestResponseStatusCounts = {
    approvedAlternativesCount: 0,
    approvedCount: 0,
    clientAcceptedCount: 0,
    deniedCount: 0,
    errorsCount: 0,
    expectationsCount: 0,
    financedCount: 0,
    fundedDateOfFinanced: '—',
    isAddedToQueue: requestStatus === 'add_queue',
    isClientAccepted: false,
    isDraft: requestStatus === 'draft',
    isError: requestStatus === 'error',
    isFinanced: false,
    isSelected: false,
    isUnverified: requestStatus === 'sms_verificating',
    selectedBankName: '',
    waitingFinancingCount: 0
  };

  const clientAcceptedBankNames: (string | undefined)[] = [];
  const financedBankNames: {
    bankName: string | undefined;
    fundedDate: string | null;
  }[] = [];
  const waitingFinancingBankNames: (string | undefined)[] = [];

  for (const response of responses) {
    const { status, bankName, fundedDate } = response;

    if (status === 'accepted_alternative') {
      result.approvedAlternativesCount++;
    }

    if (
      status === 'approved' ||
      status === 'client_accepted' ||
      status === 'pre_approved'
    ) {
      result.approvedCount++;
    }

    if (status === 'client_accepted') {
      result.clientAcceptedCount++;
      clientAcceptedBankNames.push(bankName);
    }

    if (
      status === 'cancelled' ||
      status === 'declined' ||
      status === 'bank_response_timeout'
    ) {
      result.deniedCount++;
    }

    if (status === 'error') {
      result.errorsCount++;
    }

    if (
      status === 'on_rework' ||
      status === 'on_verification' ||
      status === 'waiting_bank_response'
    ) {
      result.expectationsCount++;
    }

    if (status === 'financed') {
      result.financedCount++;
      financedBankNames.push({ bankName, fundedDate });
    }

    if (SELECTED_BANK_STATUSES.includes(status) && status !== 'financed') {
      result.waitingFinancingCount++;
      waitingFinancingBankNames.push(bankName);
    }
  }

  result.isClientAccepted = result.clientAcceptedCount > 0;
  result.isFinanced = result.financedCount > 0;
  result.isSelected = result.waitingFinancingCount > 0;

  if (result.isClientAccepted) {
    result.selectedBankName = clientAcceptedBankNames[0] || '';
  } else if (result.isFinanced) {
    result.selectedBankName = financedBankNames[0]?.bankName || '';
  } else if (result.isSelected) {
    result.selectedBankName = waitingFinancingBankNames[0] || '';
  }

  if (result.financedCount > 0) {
    result.fundedDateOfFinanced = dayjs(financedBankNames[0].fundedDate).format(
      'DD.MM.YYYY'
    );
  }

  return result;
};

export default requestResponsesCounter;
